import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from 'cms/core/shared/AdminLayout';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    IconButton,
    Paper,
    Stack,
    Typography,
    TextField,
    Switch,
    FormControlLabel,
    Alert,
    Menu,
    MenuItem,
    ListItemText
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import ShareIcon from '@mui/icons-material/Share';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useQuery } from 'react-query';
import { HttpService } from 'core/services/http';
import { CVHttpService } from 'features/feature_cv/services';
import { Utils } from 'core/utils';
import AdminCVEditDialog from '../components/dialogs/AdminCVEditDialog';
import ExperiencesSection from '../components/sections/ExperiencesSection';
import EducationSection from '../components/sections/EducationSection';
import SkillsSection from '../components/sections/SkillsSection';
import LanguagesSection from '../components/sections/LanguagesSection';
import CertificatesSection from '../components/sections/CertificatesSection';
import ReferencesSection from '../components/sections/ReferencesSection';

const formatText = (value, fallback = '—') => {
    if (!Utils.normalizeText) return value ?? fallback;
    return Utils.normalizeText(value, fallback);
};

function CVDetailsPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [isEditDialogOpen, setEditDialogOpen] = useState(false);
    const [shareURL, setShareURL] = useState('');
    const [copied, setCopied] = useState(false);
    const [selectedStyle, setSelectedStyle] = useState('style2');
    const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);

    const { data: cv, isLoading, refetch } = useQuery(
        ['admin_cv_detail', id],
        async () => {
            const service = new HttpService();
            return await service.get(`/job/cv/${id}`, null);
        },
        { enabled: !!id }
    );

    React.useEffect(() => {
        if (cv?.share_uuid) {
            const url = CVHttpService.getShareableURL(cv.share_uuid);
            setShareURL(url);
        }
    }, [cv]);

    const handleDownloadPDF = async (style = selectedStyle) => {
        try {
            setSelectedStyle(style);
            await CVHttpService.downloadPDF(id, style);
            setDownloadMenuAnchor(null);
        } catch (err) {
            alert('Failed to download PDF');
        }
    };

    const handleDownloadMenuOpen = (event) => {
        setDownloadMenuAnchor(event.currentTarget);
    };

    const handleDownloadMenuClose = () => {
        setDownloadMenuAnchor(null);
    };

    const cvStyles = [
        { value: 'style1', label: 'Style 1' },
        { value: 'style2', label: 'Style 2 (Default)' },
        { value: 'style3', label: 'Style 3 (Minimalist)' },
    ];

    const handleCopyURL = async () => {
        try {
            await navigator.clipboard.writeText(shareURL);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            alert('Failed to copy URL');
        }
    };

    const handleSaveCV = async (payload) => {
        if (!cv?.id) return;
        try {
            await CVHttpService.saveCV({ ...payload, id: cv.id });
            await refetch();
        } catch (err) {
            alert('Failed to update CV');
        }
    };

    const handleStatusToggle = async (event) => {
        const newStatus = event.target.checked;
        try {
            await CVHttpService.saveCV({ id: cv.id, status: newStatus });
            await refetch();
        } catch (err) {
            alert('Failed to update CV status');
        }
    };

    if (isLoading) {
        return (
            <AdminLayout pageTitle="CV Details">
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
                    <CircularProgress />
                </Box>
            </AdminLayout>
        );
    }

    if (!cv) {
        return (
            <AdminLayout pageTitle="CV Details">
                <Paper sx={{ p: 4, borderRadius: 3 }}>
                    <Typography variant="h6">CV not found</Typography>
                    <Button sx={{ mt: 2 }} onClick={() => navigate(-1)}>
                        Back to CVs
                    </Button>
                </Paper>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout pageTitle={formatText(cv.title)}>
            <Stack spacing={3}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <IconButton onClick={() => navigate(-1)}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h4" fontWeight={700} color="text.primary">
                            {formatText(cv.title)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {formatText(`${cv.name || ''} ${cv.last_name || ''}`.trim() || 'No name')}
                        </Typography>
                    </Box>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={cv.status !== false}
                                onChange={handleStatusToggle}
                                color="primary"
                            />
                        }
                        label={cv.status !== false ? 'Active' : 'Inactive'}
                    />
                    <Button
                        variant="outlined"
                        startIcon={<EditOutlinedIcon />}
                        onClick={() => setEditDialogOpen(true)}
                    >
                        Edit CV
                    </Button>
                    {shareURL && (
                        <Button
                            variant="outlined"
                            startIcon={<ShareIcon />}
                            onClick={handleCopyURL}
                            color={copied ? 'success' : 'primary'}
                        >
                            {copied ? 'Copied!' : 'Copy Share URL'}
                        </Button>
                    )}
                    <Box sx={{ position: 'relative', display: 'inline-block' }}>
                        <Button
                            variant="contained"
                            startIcon={<DownloadIcon />}
                            endIcon={<ArrowDropDownIcon />}
                            onClick={handleDownloadMenuOpen}
                        >
                            Download PDF
                        </Button>
                        <Menu
                            anchorEl={downloadMenuAnchor}
                            open={Boolean(downloadMenuAnchor)}
                            onClose={handleDownloadMenuClose}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                        >
                            {cvStyles.map((style) => (
                                <MenuItem
                                    key={style.value}
                                    onClick={() => handleDownloadPDF(style.value)}
                                    selected={selectedStyle === style.value}
                                >
                                    <ListItemText primary={style.label} />
                                </MenuItem>
                            ))}
                        </Menu>
                    </Box>
                </Stack>

                {shareURL && (
                    <Paper sx={{ p: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                        <Typography variant="body2" fontWeight={600} gutterBottom>
                            Shareable URL:
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                            {shareURL}
                        </Typography>
                    </Paper>
                )}

                <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="h6" gutterBottom fontWeight={600} color="text.primary">
                                Contact Information
                            </Typography>
                            <Stack spacing={1}>
                                <Typography><strong>Email:</strong> {formatText(cv.email)}</Typography>
                                <Typography><strong>Phone:</strong> {formatText(cv.phone)}</Typography>
                                <Typography><strong>Website:</strong> {formatText(cv.website)}</Typography>
                                <Typography><strong>City:</strong> {formatText(cv.city)}</Typography>
                                <Typography><strong>Address:</strong> {formatText(cv.address)}</Typography>
                            </Stack>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="h6" gutterBottom fontWeight={600} color="text.primary">
                                Additional Information
                            </Typography>
                            <Stack spacing={1}>
                                <Typography><strong>Owner:</strong> {cv.user?.email || '—'}</Typography>
                                <Typography><strong>Created:</strong> {cv.created_at ? Utils.formatDate(cv.created_at) : '—'}</Typography>
                                <Typography><strong>Last Updated:</strong> {cv.updated_at ? Utils.formatDate(cv.updated_at) : '—'}</Typography>
                            </Stack>
                        </Grid>
                        {cv.description && (
                            <Grid item xs={12}>
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="h6" gutterBottom fontWeight={600} color="text.primary">
                                    Description
                                </Typography>
                                <Typography variant="body1" color="text.secondary">
                                    {formatText(cv.description)}
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                </Paper>

                {/* CV Sections */}
                {cv?.id && (
                    <>
                        <ExperiencesSection cvId={cv.id} />
                        <EducationSection cvId={cv.id} />
                        <SkillsSection cvId={cv.id} />
                        <LanguagesSection cvId={cv.id} />
                        <CertificatesSection cvId={cv.id} />
                        <ReferencesSection cvId={cv.id} />
                    </>
                )}
            </Stack>

            <AdminCVEditDialog
                open={isEditDialogOpen}
                onClose={() => setEditDialogOpen(false)}
                cv={cv}
                onSave={handleSaveCV}
            />
        </AdminLayout>
    );
}

export default CVDetailsPage;

